// app_client/src/components/modals/FinancialAccountFormModal.jsx
import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import useFinancialStore from '../../store/useFinancialStore';
import { X, Save, Landmark, Banknote, CreditCard } from 'lucide-react';

// initialData prop olarak eklendi
const FinancialAccountFormModal = ({ isOpen, onClose, initialData = null }) => {
  const { addAccount, updateAccount } = useFinancialStore(); // updateAccount eklendi
  
  const { register, handleSubmit, reset, control, setValue, formState: { errors } } = useForm({
    defaultValues: { type: 'Cash', currency: 'TRY' }
  });

  // Edit Modu Kontrolü: initialData varsa verileri forma bas, yoksa temizle
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Edit modu: initialData içindeki verileri form alanlarına yükle
        reset(initialData);
      } else {
        // Yeni kayıt modu: Formu varsayılan değerlere sıfırla
        reset({ type: 'Cash', currency: 'TRY', name: '', iban: '', bankName: '', branchName: '' });
      }
    }
  }, [isOpen, initialData, reset]);

  const formatIBAN = (value) => {
    if (!value) return "";
    const clearValue = value.replace(/\s+/g, "").toUpperCase();
    const formatted = clearValue.replace(/(.{4})/g, "$1 ").trim();
    return formatted.slice(0, 32);
  };

  const selectedType = useWatch({ control, name: "type", defaultValue: "Cash" });
  const selectedCurrency = useWatch({ control, name: "currency", defaultValue: "TRY" });

  const onSubmit = async (data) => {
    let success;
    if (initialData?._id) {
      // Eğer ID varsa güncelleme yap
      success = await updateAccount(initialData._id, data);
    } else {
      // ID yoksa yeni ekleme yap
      success = await addAccount(data);
    }

    if (success) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 border border-white/20">
        
        {/* Header */}
        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
          <div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight">
              {initialData ? 'Hesabı Düzenle' : 'Yeni Hesap'}
            </h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
              {initialData ? 'Mevcut Kaydı Güncelle' : 'Finansal Kaynak Tanımla'}
            </p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-white rounded-2xl transition-colors text-slate-400 hover:text-slate-600 shadow-sm border border-transparent hover:border-slate-100">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
          
          {/* Hesap Tipi Seçimi */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Hesap Türü</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'Cash', label: 'Kasa', icon: <Banknote size={18} /> },
                { id: 'Bank', label: 'Banka', icon: <Landmark size={18} /> },
                { id: 'CreditCard', label: 'Kredi Kartı', icon: <CreditCard size={18} /> }
              ].map((t) => (
                <label key={t.id} className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 cursor-pointer transition-all ${selectedType === t.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}>
                  <input type="radio" value={t.id} {...register("type")} className="hidden" />
                  {t.icon}
                  <span className="text-[10px] font-black uppercase tracking-tighter">{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Döviz Seçimi */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Para Birimi</label>
            <div className="flex p-1.5 bg-slate-100 rounded-2xl gap-1">
              {['TRY', 'USD', 'EUR', 'GBP'].map((curr) => (
                <label key={curr} className={`flex-1 text-center py-2.5 rounded-xl cursor-pointer text-xs font-black transition-all ${selectedCurrency === curr ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
                  <input type="radio" value={curr} {...register("currency")} className="hidden" />
                  {curr}
                </label>
              ))}
            </div>
          </div>

          {/* Form Alanları */}
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Hesap Adı</label>
              <input 
                {...register("name", { required: "Hesap adı zorunludur" })}
                placeholder="Örn: Garanti Ticari"
                className="w-full mt-1 px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-slate-200 font-bold text-slate-700 placeholder:text-slate-300 transition-all shadow-inner"
              />
              {errors.name && <p className="text-rose-500 text-[10px] font-bold mt-2 ml-1 uppercase tracking-tighter">{errors.name.message}</p>}
            </div>

            {selectedType === 'Bank' && (
              <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-4 duration-300">
                <div className="col-span-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">IBAN</label>
                  <input 
                    {...register("iban", { required: selectedType === 'Bank' })}
                    placeholder="TR00 0000..."
                    onChange={(e) => {
                      const formatted = formatIBAN(e.target.value);
                      setValue("iban", formatted);
                    }}
                    className="w-full mt-1 px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-slate-200 font-mono font-bold text-slate-700 shadow-inner"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Banka Adı</label>
                  <input {...register("bankName")} className="w-full mt-1 px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-slate-200 font-bold text-slate-700 shadow-inner" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Şube</label>
                  <input {...register("branchName")} className="w-full mt-1 px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-slate-200 font-bold text-slate-700 shadow-inner" />
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors">
              Vazgeç
            </button>
            <button type="submit" className="flex-2 flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95">
              <Save size={18} /> {initialData ? 'Güncelle' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancialAccountFormModal;